import React from "react";

interface HourlyData {
    hour: string;
    count: number;
    timestamp: Date;
}

interface Props {
    data: HourlyData[];
    maxCount: number;
    loading: boolean;
    color: string;
    timeScale: 'day' | 'week';
    dataLabel: string; // e.g., "login" or "session"
}

function TimeSeriesGraph(props: Props) {
    const graphHeight = 200;
    const graphPadding = 40;

    if (props.loading) {
        return <div style={{ textAlign: "center", padding: "2em" }}>Loading graph...</div>;
    }

    if (props.data.length === 0) {
        return <div style={{ textAlign: "center", padding: "2em" }}>No data available</div>;
    }

    return (
        <div>
            <svg 
                width="100%" 
                height={graphHeight + graphPadding}
            >
                {/* Grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
                    const y = graphPadding + (graphHeight - graphPadding) * (1 - fraction);
                    return (
                        <g key={fraction}>
                            <line
                                x1="18"
                                y1={y}
                                x2="100%"
                                y2={y}
                                stroke="#e0e0e0"
                                strokeWidth="1"
                            />
                            <text
                                x="5"
                                y={y + 4}
                                fontSize="10"
                                fill="#666"
                            >
                                {Math.round(props.maxCount * fraction)}
                            </text>
                        </g>
                    );
                })}

                {/* Bar chart */}
                {props.data.map((data, index) => {
                    const leftMargin = 35; // pixels for Y-axis
                    const availableWidth = 100 - (leftMargin / 10); // Approximate percentage
                    const barWidth = (availableWidth / props.data.length) * 0.8;
                    const x = leftMargin / 10 + (index / props.data.length) * availableWidth;
                    const barHeight = (data.count / props.maxCount) * (graphHeight - graphPadding);
                    const y = graphHeight - barHeight;

                    return (
                        <g key={index}>
                            <rect
                                x={`${x}%`}
                                y={y}
                                width={`${barWidth}%`}
                                height={barHeight}
                                fill={props.color}
                                opacity="0.8"
                            >
                                <title>{`${data.hour}: ${data.count} ${props.dataLabel}${data.count !== 1 ? 's' : ''}`}</title>
                            </rect>
                            {/* Show label - every 2 hours for day view, all labels for week view */}
                            {(props.timeScale === 'week' || index % 2 === 0) && (
                                <text
                                    x={`${x + barWidth / 2}%`}
                                    y={graphHeight + 15}
                                    fontSize="11"
                                    fill="#666"
                                    textAnchor="middle"
                                >
                                    {data.hour}
                                </text>
                            )}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

export default TimeSeriesGraph;
